import { useNavigate } from '@tanstack/react-router'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { notifyError } from '@/components/notify'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthedMe, useSwitchTenant } from '@/features/auth/api'
import { cn, initials } from '@/lib/utils'
import { CreateOrganizationDialog } from './create-organization-dialog'

function TenantBadge({ name, className }: { name: string; className?: string }) {
  return (
    <span className={cn('flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-gradient text-[11px] font-semibold text-white shadow-sm', className)} aria-hidden>
      {initials(name)}
    </span>
  )
}

export function TenantSwitcher() {
  const me = useAuthedMe()
  const switchTenant = useSwitchTenant()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)
  const current = me.current_tenant
  const plan = me.subscription?.plan.name

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex h-9 max-w-[16rem] min-w-0 items-center gap-2.5 rounded-lg px-1.5 pr-2 text-left outline-none transition hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Switch organization"
          >
            <TenantBadge name={current?.name ?? '?'} />
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-[13.5px] font-semibold">{current?.name ?? 'No organization'}</span>
              {plan && <span className="block truncate text-[11px] text-muted-foreground">{plan} plan</span>}
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel>Organizations</DropdownMenuLabel>
          <div className="max-h-72 overflow-y-auto scrollbar-thin">
            {me.memberships.map((m) => {
              const active = m.tenant.id === current?.id
              return (
                <DropdownMenuItem
                  key={m.tenant.id}
                  disabled={switchTenant.isPending}
                  onSelect={() => {
                    if (active) return
                    switchTenant.mutate(m.tenant.id, {
                      onSuccess: (next) => {
                        toast.success(`Switched to ${m.tenant.name}`)
                        void navigate({ to: next.current_tenant ? '/' : '/onboarding' })
                      },
                      onError: (e) => notifyError(e),
                    })
                  }}
                  className="gap-2.5 py-2"
                >
                  <TenantBadge name={m.tenant.name} className="size-6 text-[10px]" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-foreground">{m.tenant.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{m.role.name}</span>
                  </span>
                  {active && <Check className="text-primary" />}
                </DropdownMenuItem>
              )
            })}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setCreateOpen(true)} className="py-2">
            <span className="flex size-6 items-center justify-center rounded-md border border-dashed">
              <Plus className="size-3.5" />
            </span>
            Create organization
            {me.memberships.length === 0 && <Badge className="ml-auto">New</Badge>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateOrganizationDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  )
}
